import { useCallback, useMemo } from 'react';

import { MedicalSpecialty, MedicalSpecialtyQuery, SpecialtyLevel } from '@dreryk/egabinet-personnel-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { Bean, ValidationRules } from '@dreryk/react-common';
import { InteractiveTable, InteractiveTableCellProps } from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { personnelTranslationKeys } from '../../../../../translations';

export const medicalSpecialtySearchCreator = (searchText: string | null) => {
   // eslint-disable-next-line @typescript-eslint/require-await
   return async (): CqrsQueryPerformResult<MedicalSpecialty[]> =>
      performCqrsQuerySearch<MedicalSpecialty>(() => {
         const query = new MedicalSpecialtyQuery();
         if (searchText) {
            query.name = TextPattern.anywhereIgnoreCase(searchText);
         }
         return query;
      });
};

type DoctorSpecialtyBean = {
   medicalSpecialty: MedicalSpecialty | undefined;
   specialtyLevel: SpecialtyLevel | undefined;
};

const isDoctorSpecialtyAlreadyOnList = (
   doctorSpecialtiesList: DoctorSpecialtyBean[],
   medicalSpecialty: MedicalSpecialty
): boolean =>
   doctorSpecialtiesList.some((bean) => bean.medicalSpecialty && bean.medicalSpecialty?.id === medicalSpecialty?.id);

const isAtLeastOneDoctorSpecialtyOnList = (doctorSpecialties: DoctorSpecialtyBean[]): boolean =>
   doctorSpecialties.some((bean) => bean.medicalSpecialty?.id && bean.specialtyLevel);

export const MedicalSpecialtyAutosuggestCell = (
   props: Omit<InteractiveTableCellProps<MedicalSpecialty>, 'name' | 'testId'>
): JSX.Element => {
   const itemRenderer = useCallback((medicalSpecialty: MedicalSpecialty) => <>{medicalSpecialty.name}</>, []);
   const textInputValueExtractor = useCallback((medicalSpecialty: MedicalSpecialty) => `${medicalSpecialty.name}`, []);
   const keyExtractor = useCallback((medicalSpecialty: MedicalSpecialty) => medicalSpecialty.id, []);

   const validationRules = useMemo<ValidationRules<Bean>>(
      () => ({
         required: true,
         validate: (data, getValue) => {
            const currentSpecialties = getValue('doctorSpecialties') as DoctorSpecialtyBean[];
            const medicalSpecialty = data as MedicalSpecialty;
            const specialtiesWithoutCurrentSpecialties = currentSpecialties.filter(
               (__, index) => index !== props.index
            );

            if (isDoctorSpecialtyAlreadyOnList(specialtiesWithoutCurrentSpecialties, medicalSpecialty)) {
               return personnelTranslationKeys.specialtiesTable.specialtyAlreadyOnList;
            }

            if (isAtLeastOneDoctorSpecialtyOnList(currentSpecialties)) {
               return undefined;
            }

            if (!medicalSpecialty) {
               return personnelTranslationKeys.specialtiesTable.completionRequired;
            }

            return undefined;
         },
      }),
      [props.index]
   );

   return (
      <InteractiveTable.AutosuggestCell<MedicalSpecialty>
         testId={'medicalSpecialty'}
         name={'medicalSpecialty'}
         index={props.index}
         defaultValue={props.defaultValue}
         validationRules={validationRules}
         itemRenderer={itemRenderer}
         textInputValueExtractor={textInputValueExtractor}
         keyExtractor={keyExtractor}
         placeholder={personnelTranslationKeys.specialtiesTable.searchCodeOrName}
         searchCreator={medicalSpecialtySearchCreator}
         minChars={3}
      />
   );
};
